import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../services/api'

interface UseApiEntityOptions<T> {
  readonly path: string
  readonly listPath: string
  readonly defaultValue: T
  // Phase 5.9.6: optional payload transform so form pages with
  // numeric/date fields can marshal strings into the shape the
  // backend expects without re-implementing save() themselves.
  readonly transformPayload?: (data: T) => unknown
}

interface UseApiEntityResult<T> {
  readonly data: T
  readonly setData: (data: T) => void
  readonly isNew: boolean
  readonly loading: boolean
  readonly saving: boolean
  readonly error: string | null
  readonly success: string | null
  readonly setError: (error: string | null) => void
  readonly save: () => Promise<void>
  readonly cancel: () => void
  readonly update: <K extends keyof T>(field: K, value: T[K]) => void
  readonly refresh: () => Promise<void>
}

export function useApiEntity<T>({
  path,
  listPath,
  defaultValue,
  transformPayload,
}: UseApiEntityOptions<T>): UseApiEntityResult<T> {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [data, setData] = useState<T>(defaultValue)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchEntity = useCallback(async () => {
    if (isNew) return
    setLoading(true)
    try {
      const result = await api.get<T>(`${path}/${id}`)
      if (result.data) setData(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [id, isNew, path])

  useEffect(() => {
    fetchEntity()
  }, [fetchEntity])

  const save = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const payload = transformPayload ? transformPayload(data) : data
      if (isNew) {
        await api.post(path, payload)
        navigate(listPath)
      } else {
        await api.put(`${path}/${id}`, payload)
        setSuccess('Saved successfully')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const update = <K extends keyof T>(field: K, value: T[K]) => {
    setData({ ...data, [field]: value })
  }

  const cancel = () => navigate(listPath)

  return { data, setData, isNew, loading, saving, error, success, setError, save, cancel, update, refresh: fetchEntity }
}
